import { useMemo, useRef, useState } from 'react'
import { useTranslation, type TFunction } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Play, Square, Check, ChevronDown, ChevronUp, Star } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { accountQueries } from '@/services/accountApi'
import { AvatarPlaceholder } from '@/components/ui/avatar-placeholder'
import type { AgentVoice } from '@/types/account'

interface VoiceSelectorProps {
  value: string | null
  onChange: (voiceId: string) => void
  disabled?: boolean
  /** Render without Card wrapper — just the voice grid */
  bare?: boolean
}

// Compact card used for non-recommended voices and dashboard grid
function CompactVoiceCard({
  voice,
  isSelected,
  isPlaying,
  isDefault,
  disabled,
  onSelect,
  onTogglePreview,
  t,
}: {
  voice: AgentVoice
  isSelected: boolean
  isPlaying: boolean
  isDefault: boolean
  disabled?: boolean
  onSelect: () => void
  onTogglePreview: () => void
  t: TFunction
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`group relative flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-all hover:bg-muted/50 ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <AvatarPlaceholder seed={voice.id} size={32} variant="botttsNeutral" className="shrink-0" />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium capitalize text-xs">
            {voice.id}
          </span>
          {isDefault && (
            <Badge variant="secondary" className="text-[9px] px-1 py-0 font-medium">
              {t('voices.default')}
            </Badge>
          )}
          {isSelected && (
            <Check className="h-3 w-3 shrink-0 text-primary" />
          )}
        </div>
        <p className="truncate text-[10px] text-muted-foreground">
          {voice.expressedGender === 'feminine'
            ? t('voices.feminine')
            : t('voices.masculine')}{' '}
          · {voice.accent}
        </p>
      </div>

      <button
        type="button"
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
          isPlaying
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          onTogglePreview()
        }}
        disabled={disabled}
      >
        {isPlaying ? (
          <Square className="h-3 w-3" />
        ) : (
          <Play className="h-3 w-3 ml-0.5" />
        )}
      </button>
    </button>
  )
}

// Featured card used for recommended voices — larger with more visual weight
function FeaturedVoiceCard({
  voice,
  isSelected,
  isPlaying,
  isDefault,
  disabled,
  onSelect,
  onTogglePreview,
  t,
}: {
  voice: AgentVoice
  isSelected: boolean
  isPlaying: boolean
  isDefault: boolean
  disabled?: boolean
  onSelect: () => void
  onTogglePreview: () => void
  t: TFunction
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`relative p-4 rounded-xl text-left transition-all duration-200 ${
        isSelected
          ? 'bg-card border-2 border-primary shadow-md'
          : 'bg-muted/30 border-2 border-transparent hover:border-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className="flex items-center justify-between mb-2.5">
        <AvatarPlaceholder seed={voice.id} size={36} variant="botttsNeutral" className="rounded-lg" />
        <div className="flex items-center gap-1.5">
          {isDefault && (
            <Badge variant="secondary" className="text-[9px] px-1.5 py-0 font-medium">
              {t('voices.default')}
            </Badge>
          )}
          {isSelected && (
            <Check className="h-4 w-4 text-primary" />
          )}
        </div>
      </div>
      <h3 className="font-bold text-sm capitalize">{voice.id}</h3>
      <p className="text-[10px] text-muted-foreground mt-0.5">
        {voice.expressedGender === 'feminine'
          ? t('voices.feminine')
          : t('voices.masculine')}{' '}
        · {voice.accent}
      </p>
      <button
        type="button"
        className={`mt-2.5 flex items-center gap-1.5 text-xs font-bold transition-colors ${
          isPlaying ? 'text-primary' : 'text-muted-foreground hover:text-primary'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          onTogglePreview()
        }}
        disabled={disabled}
      >
        {isPlaying ? (
          <Square className="h-3 w-3" />
        ) : (
          <Play className="h-3 w-3 ml-0.5" />
        )}
        {isPlaying ? t('voices.stop') : t('voices.preview')}
      </button>
    </button>
  )
}

const voiceAudioFiles = import.meta.glob('@/assets/voice-examples/audio-*.mp3', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>

// Build a voiceId → URL map: "/src/assets/voice-examples/audio-arugula.mp3" → "arugula"
const voiceAudioUrls: Record<string, string> = {}
for (const [path, url] of Object.entries(voiceAudioFiles)) {
  const match = path.match(/audio-(.+)\.mp3$/)
  if (match) voiceAudioUrls[match[1]] = url
}

function useVoicePreview() {
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    setPlayingId(null)
  }

  const togglePreview = (voiceId: string) => {
    if (playingId === voiceId) {
      stopAudio()
      return
    }
    stopAudio()
    const url = voiceAudioUrls[voiceId]
    if (!url) return
    const audio = new Audio(url)
    audioRef.current = audio
    setPlayingId(voiceId)
    audio.play()
    audio.onended = () => setPlayingId(null)
    audio.onerror = () => setPlayingId(null)
  }

  return { playingId, togglePreview }
}

/** Voice grid content — no wrapper, used by both Card and bare modes */
function VoiceGrid({
  value,
  onChange,
  disabled,
}: {
  value: string | null
  onChange: (voiceId: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('app')
  const { data, isLoading, error } = useQuery(accountQueries.agentVoices())
  const [showAll, setShowAll] = useState(false)
  const { playingId, togglePreview } = useVoicePreview()

  const voices = data?.voices ?? []
  const defaultVoice = data?.defaultVoice ?? 'spinach'

  const { recommended, others } = useMemo(() => {
    const rec: AgentVoice[] = []
    const oth: AgentVoice[] = []
    for (const v of voices) {
      if (v.recommended) rec.push(v)
      else oth.push(v)
    }
    return { recommended: rec, others: oth }
  }, [voices])

  if (error) return null

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Recommended voices — featured cards */}
      {recommended.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Star className="h-3 w-3" />
            {t('voices.recommended')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {recommended.map((voice) => (
              <FeaturedVoiceCard
                key={voice.id}
                voice={voice}
                isSelected={value === voice.id}
                isPlaying={playingId === voice.id}
                isDefault={voice.id === defaultVoice}
                disabled={disabled}
                onSelect={() => onChange(voice.id)}
                onTogglePreview={() => togglePreview(voice.id)}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other voices — compact cards, collapsible */}
      {others.length > 0 && (
        <div className="space-y-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                <ChevronUp className="h-3 w-3" />
                {t('voices.showLess')}
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                {t('voices.showAll')} ({others.length})
              </>
            )}
          </Button>
          {showAll && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {others.map((voice) => (
                <CompactVoiceCard
                  key={voice.id}
                  voice={voice}
                  isSelected={value === voice.id}
                  isPlaying={playingId === voice.id}
                  isDefault={voice.id === defaultVoice}
                  disabled={disabled}
                  onSelect={() => onChange(voice.id)}
                  onTogglePreview={() => togglePreview(voice.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function VoiceSelector({ value, onChange, disabled, bare }: VoiceSelectorProps) {
  const { t } = useTranslation('app')

  if (bare) {
    return <VoiceGrid value={value} onChange={onChange} disabled={disabled} />
  }

  return (
    <Card flush>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('voices.title')}</CardTitle>
        <CardDescription>{t('voices.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <VoiceGrid value={value} onChange={onChange} disabled={disabled} />
      </CardContent>
    </Card>
  )
}
